import { GitBranch } from "lucide-react";
import Stat from "./Stat";

const VALENCE = {
  positive: "text-zinc-100",
  negative: "text-[#ff8b8b]",
  mixed: "text-[#e0b55c]",
  unknown: "text-zinc-500",
};

/** Emergent sub-needs the Cartographer named during the interview, grouped by parent. */
export const FacetsPanel = ({ m }) => {
  const facets = m?.facets || [];
  const s = m?.facet_summary;
  const parents = [];
  facets.forEach((f) => {
    const row = parents.find((p) => p.parent === f.parent);
    if (row) row.items.push(f);
    else parents.push({ parent: f.parent, parent_name: f.parent_name, tier: f.tier, items: [f] });
  });
  parents.sort((a, b) => a.tier - b.tier || a.parent.localeCompare(b.parent));

  return (
    <div data-testid="admin-facets-panel" className="space-y-8">
      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-5">
        <Stat
          testid="facet-count"
          k="Facets named"
          v={s?.count ?? 0}
          sub={`gate needs ${s?.need ?? 0}`}
        />
        <Stat
          k="Parents covered"
          v={s?.parents ?? 0}
          sub={`gate needs ${s?.need_parents ?? 0} distinct`}
        />
        <Stat
          k="Cap per parent"
          v={s?.max_per_parent ?? 4}
          sub="sharpest four survive"
        />
        <Stat
          testid="facet-promoted-count"
          k="Graduated"
          v={s?.promoted ?? 0}
          sub={`${s?.promotion_rule?.mentions ?? 3} mentions at ${Math.round(
            s?.promotion_rule?.salience ?? 60
          )}+ salience`}
        />
        <Stat
          k="Majors filled"
          v={`${m?.coverage?.filled ?? 0}/${m?.coverage?.total ?? 46}`}
          sub="the fixed skeleton"
        />
      </div>

      <p className="max-w-3xl text-xs leading-relaxed text-zinc-500">
        The {m?.coverage?.total ?? 46} major categories are the skeleton. A facet is a sub-need in
        the subject's own terms, named by the Cartographer when their account is sharper than the
        label it sits under — and it orbits its parent on the sphere. Near-duplicate wordings are
        folded together and kept as aliases, so a facet's mention count is real corroboration
        rather than the model renaming the same thing twice. A facet that keeps firing —{" "}
        {s?.promotion_rule?.mentions ?? 3} corroborations at salience{" "}
        {Math.round(s?.promotion_rule?.salience ?? 60)} or above — graduates into a standing node
        and can then carry its own influence edges, so the map can say that <em>this</em> sub-need
        is what disturbs a lower tier rather than blaming the whole category.
      </p>

      <div className="space-y-10">
        {parents.map((p) => (
          <section key={p.parent} data-testid={`facet-parent-${p.parent}`}>
            <p className="label flex items-center gap-2">
              <GitBranch size={11} strokeWidth={1.7} /> T{p.tier} · {p.parent_name} · {p.parent}
            </p>
            <ul className="mt-3">
              {p.items.map((f) => (
                <li
                  key={f.id}
                  data-testid={`facet-row-${f.id}`}
                  className="grid gap-3 border-b border-[var(--line)] py-3.5 sm:grid-cols-[1fr_auto]"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2.5">
                      <p className="text-sm text-zinc-100">{f.name}</p>
                      {f.promoted ? (
                        <span
                          data-testid={`facet-promoted-${f.id}`}
                          className="border border-[var(--line-hi)] px-1.5 py-0.5 font-data text-[9px] uppercase tracking-[0.16em] text-zinc-200"
                        >
                          standing node
                          {f.promoted_turn ? ` · turn ${f.promoted_turn}` : ""}
                        </span>
                      ) : (
                        <span className="font-data text-[9px] uppercase tracking-[0.14em] text-zinc-700">
                          {f.mentions_to_promotion > 0
                            ? `${f.mentions_to_promotion} more mention${f.mentions_to_promotion === 1 ? "" : "s"}`
                            : `needs +${f.salience_to_promotion} salience`}{" "}
                          to graduate
                        </span>
                      )}
                    </div>
                    {f.gloss && (
                      <p className="mt-1 text-xs leading-relaxed text-zinc-500">{f.gloss}</p>
                    )}
                    {f.aliases?.length > 0 && (
                      <p className="mt-1 font-data text-[10px] text-zinc-700">
                        also said as: {f.aliases.join(" · ")}
                      </p>
                    )}
                    {f.evidence?.length > 0 && (
                      <p className="mt-1.5 font-data text-[10px] italic text-zinc-600">
                        “{f.evidence[f.evidence.length - 1]}”
                      </p>
                    )}
                  </div>
                  <div className="font-data text-[10px] tabular-nums text-zinc-500 sm:text-right">
                    <span className="text-zinc-200">salience {Math.round(f.salience)}</span>
                    <span className={`ml-3 ${VALENCE[f.valence] || VALENCE.unknown}`}>
                      {f.valence}
                    </span>
                    <br />
                    <span className="text-zinc-700">
                      {f.mentions} mention{f.mentions === 1 ? "" : "s"}
                      {f.first_turn ? ` · first turn ${f.first_turn}` : ""}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {facets.length === 0 && (
          <p className="font-data text-[11px] text-zinc-600">
            Nothing named yet. Facets start appearing once the interview reaches the tier-fill
            phase and the subject gives an account sharper than a category label.
          </p>
        )}
      </div>
    </div>
  );
};

export default FacetsPanel;
